import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

const BookSpaceForm = ({ id }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "+44",
    spaceType: "hot-desk",
    numberOfPeople: "1",
    startDate: "",
    endDate: "",
    referralSource: "",
    agreeToTerms: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const countryCodes = [
    { code: "+44", label: "UK (+44)" },
    { code: "+231", label: "Liberia (+231)" },
    { code: "+234", label: "Nigeria (+234)" },
    { code: "+233", label: "Ghana (+233)" },
    { code: "+254", label: "Kenya (+254)" },
    { code: "+27", label: "South Africa (+27)" },
    { code: "+20", label: "Egypt (+20)" },
    { code: "+251", label: "Ethiopia (+251)" },
    { code: "+212", label: "Morocco (+212)" },
    { code: "+49", label: "Germany (+49)" },
    { code: "+33", label: "France (+33)" },
    { code: "+31", label: "Netherlands (+31)" },
    { code: "+34", label: "Spain (+34)" },
    { code: "+353", label: "Ireland (+353)" },
    { code: "+32", label: "Belgium (+32)" },
    { code: "+41", label: "Switzerland (+41)" },
    { code: "+1", label: "United States / Canada (+1)" },
    { code: "+971", label: "UAE (+971)" },
    { code: "+91", label: "India (+91)" },
    { code: "+65", label: "Singapore (+65)" },
    { code: "+852", label: "Hong Kong (+852)" },
    { code: "+61", label: "Australia (+61)" },
  ];

  const spaceTypes = [
    { id: "hot-desk", label: "Hot Desk" },
    { id: "meeting-room", label: "Meeting Room" },
    { id: "conference-room", label: "Conference Room" },
    { id: "suite-rental", label: "Suite Rental" },
    { id: "office-rental", label: "Office Rental" },
    { id: "virtual-space", label: "Virtual Space" },
  ];

  const peopleOptions = ["1", "2-5", "6-10", "11-20", "21-50", "50+"];

  const referralSources = [
    "Search Engine",
    "Social Media",
    "Friend/Colleague",
    "Advertisement",
    "Previous Customer",
    "Other",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    setTimeout(() => {
      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        countryCode: "+44",
        spaceType: "hot-desk",
        numberOfPeople: "1",
        startDate: "",
        endDate: "",
        referralSource: "",
        agreeToTerms: false,
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div style={styles.formSection}>
      <div style={styles.formContainer}>
        <div style={styles.formHeader}>
          <h2 style={styles.formTitle}>Book a Space</h2>
        </div>

        <div style={styles.form}>
          {/* Name Row */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label htmlFor="firstName" style={styles.label}>First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="lastName" style={styles.label}>Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                style={styles.input}
              />
            </div>
          </div>

          {/* Email */}
          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.label}>Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              style={styles.input}
            />
          </div>

          {/* Phone with Country Code + Separator */}
          <div style={styles.formGroup}>
            <label htmlFor="phone" style={styles.label}>Phone Number *</label>
            <div style={styles.phoneInputContainer}>
              <div style={styles.countryCodeWrapper}>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  style={styles.countryCodeSelect}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.label}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} color="#ffffff" style={styles.dropdownIcon} />
              </div>

              <div style={styles.separator} />

              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                required
                disabled={isSubmitting}
                style={styles.phoneInput}
              />
            </div>
          </div>

          {/* Space Type Tabs */}
          <div style={styles.formGroup}>
            <label style={styles.label}>What space do you require? *</label>
            <div style={styles.spaceTypeContainer}>
              {spaceTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  style={{
                    ...styles.spaceTypeTab,
                    ...(formData.spaceType === type.id ? styles.spaceTypeTabActive : {})
                  }}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, spaceType: type.id }))
                  }
                  disabled={isSubmitting}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Number of People */}
          <div style={styles.formGroup}>
            <label htmlFor="numberOfPeople" style={styles.label}>
              How many people need this workspace? *
            </label>
            <select
              id="numberOfPeople"
              name="numberOfPeople"
              value={formData.numberOfPeople}
              onChange={handleChange}
              required
              disabled={isSubmitting}
              style={styles.select}
            >
              {peopleOptions.map((option) => (
                <option key={option} value={option}>
                  {option} {option === "1" ? "person" : "people"}
                </option>
              ))}
            </select>
          </div>

          {/* Dates Row */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label htmlFor="startDate" style={styles.label}>Start Date *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="endDate" style={styles.label}>End Date *</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                style={styles.input}
              />
            </div>
          </div>

          {/* Referral Source */}
          <div style={styles.formGroup}>
            <label htmlFor="referralSource" style={styles.label}>How did you hear about us?</label>
            <select
              id="referralSource"
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              disabled={isSubmitting}
              style={styles.select}
            >
              <option value="">Select an option</option>
              {referralSources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </div>

          {/* Terms */}
          <div style={styles.termsContainer}>
            <p style={styles.termsText}>
              I have read and agree to the{" "}
              <a href="/terms-and-conditions" target="_blank" rel="noopener" style={styles.termsLink}>
                terms and conditions
              </a>{" "}
              (required)
            </p>
            <div style={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                required
                disabled={isSubmitting}
                style={styles.checkbox}
              />
              <label htmlFor="agreeToTerms" style={styles.checkboxLabel}>
                I agree to the terms and conditions
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button onClick={handleSubmit} disabled={isSubmitting} style={styles.submitButton}>
            {isSubmitting ? "Submitting Request..." : "Submit Booking Request"}
          </button>

          {/* Feedback Messages */}
          {submitStatus === "success" && (
            <p style={styles.successMessage}>
              Thank you! Your booking request has been sent successfully. 
              We will contact you shortly to confirm availability.
            </p>
          )}
          {submitStatus === "error" && (
            <p style={styles.errorMessage}>
              Sorry, something went wrong. Please try again or email us directly at 
              enquiries@augusttwenty.co.uk
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  formSection: {
    background: '#101015',
    padding: '80px 20px',
    color: '#fff',
    boxSizing: 'border-box',
    minHeight: '100vh',
  },
  formContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
    padding: '0 10px',
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: '50px',
  },
  formTitle: {
    fontSize: '42px',
    fontFamily: '"Playfair Display", serif',
    fontWeight: 600,
    color: '#fff',
    margin: 0,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    width: '100%',
    boxSizing: 'border-box',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
    width: '100%',
    boxSizing: 'border-box',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  label: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '10px',
    color: '#fff',
  },
  input: {
    background: '#404044',
    border: '1px solid #404044',
    borderRadius: '10px',
    padding: '14px',
    fontSize: '16px',
    color: '#fff',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  phoneInputContainer: {
    display: 'flex',
    width: '100%',
    position: 'relative',
  },
  countryCodeWrapper: {
    position: 'relative',
    width: '160px',
    minWidth: '160px',
    flexShrink: 0,
  },
  countryCodeSelect: {
    background: '#404044',
    border: '1px solid #404044',
    borderRight: 'none',
    borderRadius: '10px 0 0 10px',
    padding: '0 36px 0 14px',
    fontSize: '16px',
    color: '#fff',
    width: '100%',
    height: '48px',
    appearance: 'none',
    cursor: 'pointer',
  },
  dropdownIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  separator: {
    position: 'absolute',
    left: '160px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1px',
    height: '24px',
    backgroundColor: '#555',
    pointerEvents: 'none',
    zIndex: 1,
  },
  phoneInput: {
    background: '#404044',
    border: '1px solid #404044',
    borderRadius: '0 10px 10px 0',
    borderLeft: 'none',
    padding: '14px',
    fontSize: '16px',
    color: '#fff',
    flex: 1,
    minWidth: 0,
    boxSizing: 'border-box',
  },
  spaceTypeContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
    boxSizing: 'border-box',
  },
  spaceTypeTab: {
    background: '#404044',
    border: '1px solid #555',
    borderRadius: '999px',
    padding: '15px 10px',
    fontSize: '14px',
    color: '#FFF',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  spaceTypeTabActive: {
    background: '#FFFFFF',
    border: '1px solid #FFFFFF',
    color: '#000',
  },
  select: {
    background: '#404044',
    border: '1px solid #555',
    borderRadius: '10px',
    padding: '14px',
    fontSize: '16px',
    color: '#fff',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  termsContainer: {
    margin: 0,
    padding: '0',
    boxSizing: 'border-box',
  },
  termsText: {
    fontSize: '16px',
    color: '#fff',
    marginBottom: '15px',
  },
  termsLink: {
    color: '#2a6ecc',
    textDecoration: 'underline',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '16px',
    color: '#fff',
    cursor: 'pointer',
  },
  submitButton: {
    background: '#ffffff',
    color: '#000000',
    border: 'none',
    borderRadius: '999px',
    padding: '18px 30px',
    fontSize: '18px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    width: '100%',
    boxSizing: 'border-box',
  },
  successMessage: {
    background: 'rgba(0, 180, 0, 0.2)',
    color: '#00ff00',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
    fontWeight: 500,
  },
  errorMessage: {
    background: 'rgba(255, 0, 0, 0.2)',
    color: '#ff6666',
    padding: '15px',
    borderRadius: '10px',
    textAlign: 'center',
    fontWeight: 500,
  },
};

// Mobile responsiveness
if (typeof window !== 'undefined') {
  const updateMobileStyles = () => {
    if (window.innerWidth <= 768) {
      styles.formRow.gridTemplateColumns = '1fr';
      styles.spaceTypeContainer.gridTemplateColumns = 'repeat(2, 1fr)';
      styles.formTitle.fontSize = '32px';
      styles.formSection.padding = '60px 20px';
      styles.countryCodeWrapper.width = '140px';
      styles.countryCodeWrapper.minWidth = '140px';
      styles.separator.left = '140px';
    }
  };
  
  window.addEventListener('resize', updateMobileStyles);
  updateMobileStyles();
}

export default BookSpaceForm;